
import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { AlertTriangle, MapPin, Mail } from 'lucide-react';

const PrivacyPage = () => {
  return (
    <div className="min-h-screen flex flex-col vitra-atmosphere">
      <Header />
      <main className="flex-grow w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-12">
          <h1 className="text-5xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
          <div className="flex flex-col sm:flex-row sm:gap-6 text-gray-600">
            <p className="text-lg">Effective Date: February 3, 2026</p>
            <p className="text-lg">Last Updated: July 19, 2026</p>
          </div>
        </div>

        {/* Important Notice Banner */}
        <div className="bg-amber-50 border-l-4 border-amber-500 rounded-lg p-6 mb-8">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" strokeWidth={2} aria-hidden="true" />
            <div>
              <h3 className="font-bold text-amber-800 mb-1">Important Medical Disclaimer</h3>
              <p className="text-amber-700 text-sm leading-relaxed">
                <strong>VITRA is NOT a medical device.</strong> This platform does not diagnose, treat, cure, or prevent any medical condition or disease. The information provided through our platform is for informational purposes only and should not be considered as a substitute for professional medical advice, diagnosis, or treatment. Always seek the advice of your physician or other qualified health provider with any questions you may have regarding a medical condition.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 space-y-8">
          
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              This Privacy Policy explains how VITRA ("we", "us", or the "Company"), acting as the Data Controller, collects, uses, shares, and protects your personal data when you use VITRA (the "App").
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              We aim to comply with the Saudi Arabian Personal Data Protection Law ("PDPL") and its Implementing Regulations, as applicable, and any guidance issued by the competent authority (SDAIA).
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              Because VITRA processes health-related information, which is treated as Sensitive Personal Data, we apply enhanced safeguards and additional lawful-basis requirements as described in this Policy. By using the App, you acknowledge that you have read and understood this Policy.
            </p>
          </section>

          {/* Definitions */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Definitions</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              For the purposes of this Privacy Policy, the following terms have the meanings set out below:
            </p>
            <div className="space-y-3">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-gray-700 text-sm"><strong>Personal Data:</strong> Any data, regardless of its source or form, that may lead to identifying an individual specifically, or that makes it possible to identify them directly or indirectly (such as name, identification number, contact details, and records).</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-gray-700 text-sm"><strong>Sensitive Personal Data:</strong> Personal Data revealing health, genetic, biometric, or similar information. Your health readings and related data fall within this category and receive enhanced protection.</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-gray-700 text-sm"><strong>Processing:</strong> Any operation performed on Personal Data, including collection, recording, storage, use, sharing, transfer, and destruction.</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-gray-700 text-sm"><strong>Data Controller:</strong> VITRA, which determines the purpose and manner of Processing your Personal Data.</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-gray-700 text-sm"><strong>Service Provider (Processor):</strong> Any natural or legal person who processes Personal Data on behalf of, and under the instructions of, VITRA.</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-gray-700 text-sm"><strong>You / Data Subject:</strong> The individual to whom the Personal Data relates and who accesses or uses the App.</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-gray-700 text-sm"><strong>Competent Authority:</strong> The Saudi Data and Artificial Intelligence Authority (SDAIA), the supervisory authority for the PDPL.</p>
              </div>
            </div>
          </section>

          {/* Scope */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Scope</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              This Policy applies to personal data processed through the App and related services, including in-app and/or official support channels.
            </p>
            <div className="bg-gray-100 rounded-lg p-4">
              <p className="text-gray-700 text-sm">
                <strong>Note:</strong> This Policy does not apply to third-party websites, services, or apps that may be linked from the App.
              </p>
            </div>
          </section>

          {/* Data We Collect and Sources */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Data We Collect and Sources</h2>
            
            <div className="space-y-6">
              {/* Data You Provide or Connect */}
              <div className="bg-gray-50 rounded-lg p-5">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <span className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center text-teal-600 text-sm font-bold">4.1</span>
                  Data You Provide or Connect
                </h3>
                <p className="text-gray-700 mb-3 ml-10">We collect data you enter or connect to the App, such as:</p>
                <ul className="space-y-2 text-gray-700 ml-10">
                  <li>• <strong>Health readings:</strong> heart rate, weight, steps, blood pressure, etc.</li>
                  <li>• <strong>Manually entered readings and notes</strong></li>
                  <li>• <strong>Mood/feelings</strong> (if enabled)</li>
                  <li>• <strong>Medication tracking:</strong> medication name/dose/reminders (based on your settings)</li>
                  <li>• <strong>Emergency contact details:</strong> name and phone/contact details</li>
                </ul>
              </div>

              {/* Device Integrations */}
              <div className="bg-gray-50 rounded-lg p-5">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <span className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center text-teal-600 text-sm font-bold">4.2</span>
                  Device Integrations
                </h3>
                <p className="text-gray-700 mb-3 ml-10">
                  If you grant permission, the App may read from and/or write to these devices & services such as based on the permissions you choose.
                </p>
                {/* <p className="text-gray-700 ml-10">Read/write details: [TBD]</p> */}
                <p className="text-gray-600 text-sm mt-3 ml-10 italic">
                  You can revoke permissions at any time in your device settings.
                </p>
              </div>

              {/* Technical and Operational Data */}
              <div className="bg-gray-50 rounded-lg p-5">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <span className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center text-teal-600 text-sm font-bold">4.3</span>
                  Technical and Operational Data
                </h3>
                <p className="text-gray-700 mb-3 ml-10">
                  We may collect minimal technical data needed to operate, improve, and secure the App, such as:
                </p>
                <ul className="space-y-2 text-gray-700 ml-10">
                  <li>• Device/OS information and app version</li>
                  <li>• Crash and diagnostic logs (if enabled)</li>
                  <li>• Security-related logs to prevent misuse (e.g., unusual login attempts, if applicable)</li>
                </ul>
                {/* <p className="text-gray-600 text-sm mt-3 ml-10 italic">
                  Exact technical data collected: [TBD]
                </p> */}
              </div>

              {/* Sensitive Personal Data */}
              <div className="bg-amber-50 rounded-lg p-5 border border-amber-200">
                <h3 className="text-lg font-semibold text-amber-900 mb-3 flex items-center gap-2">
                  <span className="w-8 h-8 bg-amber-200 rounded-full flex items-center justify-center text-amber-700 text-sm font-bold">4.4</span>
                  Sensitive Personal Data
                </h3>
                <p className="text-amber-800 ml-10">
                  Health data is considered sensitive personal data. We apply enhanced safeguards and only process it under an appropriate lawful basis (often explicit consent) in line with applicable requirements.
                </p>
              </div>
            </div>
          </section>

          {/* Purposes and Lawful Bases */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Purposes and Lawful Bases</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We process personal data for the following purposes (lawful basis may vary depending on context):
            </p>
            
            <div className="space-y-4">
              <div className="border-l-4 border-teal-500 pl-4 py-2">
                <h4 className="font-semibold text-gray-900">Providing Core App Functionality</h4>
                <p className="text-gray-700 text-sm">Displaying, saving, organizing readings and summaries.</p>
                <p className="text-teal-700 text-sm mt-1"><strong>Basis:</strong> Contract/service performance + (for health data) explicit consent where required.</p>
              </div>
              
              <div className="border-l-4 border-teal-500 pl-4 py-2">
                    <h4 className="font-semibold text-gray-900">Reminders and Notifications</h4>
                <p className="text-gray-700 text-sm">E.g., medication reminders based on your settings.</p>
                <p className="text-teal-700 text-sm mt-1"><strong>Basis:</strong> Service performance/consent (depending on the notification type).</p>
              </div>
              
              <div className="border-l-4 border-teal-500 pl-4 py-2">
                <h4 className="font-semibold text-gray-900">Customer Support</h4>
                <p className="text-gray-700 text-sm">Responding to inquiries and providing assistance.</p>
                <p className="text-teal-700 text-sm mt-1"><strong>Basis:</strong> Service performance / legal obligation where applicable.</p>
              </div>
              
              <div className="border-l-4 border-teal-500 pl-4 py-2">
                <h4 className="font-semibold text-gray-900">Security and Fraud/Abuse Prevention</h4>
                <p className="text-gray-700 text-sm">Protecting accounts and systems.</p>
                <p className="text-teal-700 text-sm mt-1"><strong>Basis:</strong> Legal obligation / legitimate interest for non-sensitive data (where applicable) + vital interests if necessary.</p>
              </div>
              
              <div className="border-l-4 border-teal-500 pl-4 py-2">
                <h4 className="font-semibold text-gray-900">Legal and Regulatory Compliance</h4>
                <p className="text-gray-700 text-sm">Responding to lawful requests from competent authorities.</p>
                <p className="text-teal-700 text-sm mt-1"><strong>Basis:</strong> Legal obligation.</p>
              </div>
            </div>
            
            {/* <p className="text-gray-600 text-sm mt-4 italic">
              Additional purpose/basis detail depending on final features: [TBD]
            </p> */}
          </section>

          {/* Data Sharing and Disclosure */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Data Sharing and Disclosure</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We do not sell your personal data. We may share personal data on a need-to-know basis with:
            </p>
            
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Service Providers (Processors)</h4>
                <p className="text-gray-700 text-sm">
                  Such as cloud hosting, databases, push notifications, support tools, backups, etc. (depending on what we adopt).
                </p>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Legal Compliance</h4>
                <p className="text-gray-700 text-sm">
                  Where we are legally required or necessary to protect rights or safety.
                </p>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">With Your Consent</h4>
                <p className="text-gray-700 text-sm">
                  If you ask us to share data with a specific third party.
                </p>
              </div>
            </div>
            
            {/* <p className="text-gray-600 text-sm mt-4 italic">
              Current provider list/categories: [TBD]
            </p> */}
          </section>

          {/* International Transfers */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. International Transfers</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
            We do not transfer personal data outside Saudi Arabia unless necessary and in line with applicable legal requirements, using appropriate safeguards (e.g., contractual arrangements, risk assessments, and any required regulatory steps, as applicable).
            </p>
            {/* <p className="text-gray-600 text-sm italic">
              Hosting locations/countries (if any): [TBD]
            </p> */}
          </section>

          {/* Data Retention and Destruction */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Data Retention</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We retain personal data for as long as needed for the purposes described in this Policy, unless applicable laws require longer or allow shorter retention.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">As a general approach:</p>
            
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="text-left p-3 font-semibold text-gray-900">Data Type</th>
                    <th className="text-left p-3 font-semibold text-gray-900">Retention Period</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="p-3 text-gray-700">Health readings &amp; clinical records</td>
                    <td className="p-3 text-gray-700">Retained for care delivery until you delete your account or as required by clinical/legal obligations. Account deletion requests are processed within approximately 30 days.</td>
                  </tr>
                  <tr>
                    <td className="p-3 text-gray-700">OTP &amp; password-reset sessions</td>
                    <td className="p-3 text-gray-700">Short-lived (minutes). Expired session rows are automatically purged after a short grace period (default 7 days after expiry).</td>
                  </tr>
                  <tr>
                    <td className="p-3 text-gray-700">Mobile refresh tokens</td>
                    <td className="p-3 text-gray-700">Valid until expiry or logout/revocation. Expired or revoked tokens are automatically purged after a grace period (default 30 days).</td>
                  </tr>
                  <tr>
                    <td className="p-3 text-gray-700">Technical/security (compliance) audit logs</td>
                    <td className="p-3 text-gray-700">Retained for security monitoring and accountability. Optional automated purge may apply after a configured multi-year period; by default audit logs are not auto-purged.</td>
                  </tr>
                  <tr>
                    <td className="p-3 text-gray-700">Consent records</td>
                    <td className="p-3 text-gray-700">Retained for accountability after grant or withdrawal, including after account closure where required.</td>
                  </tr>
                  <tr>
                    <td className="p-3 text-gray-700">Support / operational data</td>
                    <td className="p-3 text-gray-700">Retained as needed to respond to inquiries and resolve issues, subject to applicable legal requirements.</td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <p className="text-gray-700 mt-4">
              After retention ends, we securely destroy or de-identify data according to our internal procedures. Automated retention jobs purge expired authentication session data on a scheduled basis.
            </p>
          </section>

          {/* Cookies and local storage */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8A. Cookies and Local Storage</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              On the web application, we use browser storage as follows. You can manage optional preferences via the cookie banner.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="text-left p-3 font-semibold text-gray-900">Category</th>
                    <th className="text-left p-3 font-semibold text-gray-900">Examples</th>
                    <th className="text-left p-3 font-semibold text-gray-900">Required?</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="p-3 text-gray-700">Necessary</td>
                    <td className="p-3 text-gray-700">Sign-in tokens, language preference, client session id (audit correlation), push subscription endpoint</td>
                    <td className="p-3 text-gray-700">Yes — required for the service to function</td>
                  </tr>
                  <tr>
                    <td className="p-3 text-gray-700">Analytics (optional)</td>
                    <td className="p-3 text-gray-700">Web performance / vitals measurement</td>
                    <td className="p-3 text-gray-700">No — only if you accept</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-gray-700 mt-4">
              The mobile application does not use website cookies. Device storage and OS permissions are used for authentication, preferences, and notifications. Processing of health data on mobile remains governed by the in-app consent form, separate from this cookie preference.
            </p>
          </section>

          {/* Data Security */}
          <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Data Security</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We implement appropriate organizational and technical measures—especially for health data—to reduce risks of unauthorized access, loss, alteration, or unlawful disclosure.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">Measures may include (as implemented):</p>
            <ul className="space-y-2 text-gray-700">
              <li className="flex gap-3">
                <span className="text-teal-600 font-bold">✓</span>
                <span>Access control</span>
              </li>
              <li className="flex gap-3">
                <span className="text-teal-600 font-bold">✓</span>
                <span>Encryption in transit</span>
              </li>
              <li className="flex gap-3">
                <span className="text-teal-600 font-bold">✓</span>
                <span>Vulnerability management</span>
              </li>
              <li className="flex gap-3">
                <span className="text-teal-600 font-bold">✓</span>
                <span>Logging/monitoring</span>
              </li>
              <li className="flex gap-3">
                <span className="text-teal-600 font-bold">✓</span>
                <span>Periodic security testing</span>
              </li>
            </ul>
            {/* <p className="text-gray-600 text-sm mt-4 italic">
              Current controls details: [TBD]
            </p> */}
          </section>

                {/* Data Subject Rights */}
          <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Data Subject Rights</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              You may have rights under applicable law, including:
            </p>
            
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-1">Being Informed</h4>
                <p className="text-gray-700 text-sm">Right to be informed about processing.</p>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-1">Access</h4>
                <p className="text-gray-700 text-sm">Accessing your data and obtaining a copy (subject to conditions).</p>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-1">Correction</h4>
                <p className="text-gray-700 text-sm">Correcting/updating data.</p>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-1">Destruction</h4>
                <p className="text-gray-700 text-sm">Requesting destruction when there is no lawful/contractual need to retain it.</p>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-1">Data Portability</h4>
                <p className="text-gray-700 text-sm">Receiving data in a readable format (where applicable).</p>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-1">Withdraw Consent</h4>
                <p className="text-gray-700 text-sm">Withdrawing consent at any time (without affecting prior lawful processing).</p>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4 md:col-span-2">
                <h4 className="font-semibold text-gray-900 mb-1">Objection</h4>
                <p className="text-gray-700 text-sm">Objecting to certain processing where applicable.</p>
              </div>
            </div>
            
            <div className="bg-teal-50 rounded-lg p-4 border border-teal-100">
              <p className="text-teal-800 text-sm mb-2"><strong>How to submit requests:</strong> androidadmin@aces-co.com · appleadmin@aces-co.com</p>
              <p className="text-teal-800 text-sm mb-2"><strong>Timeline:</strong> We generally respond within 30 days and may extend where permitted, with notice.</p>
              <p className="text-teal-800 text-sm"><strong>Note:</strong> We may request identity verification before fulfilling requests.</p>
            </div>
          </section>

          {/* Third-Party Data */}
          <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Third-Party Data (Emergency Contacts)</h2>
            <p className="text-gray-700 leading-relaxed">
              If you add an emergency contact, you confirm you are authorized to provide that information for in-app use and app-related emergency purposes. You can remove it at any time within the App.
            </p>
          </section>

          {/* Children's Data */}
          <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Children's Data</h2>
            <p className="text-gray-700 leading-relaxed">
              The App is for users above age 18 years old. 
              Where a child's (under 18 years old) data is involved, we may request proof of guardian authority as required.
            </p>
          </section>

          {/* Personal Data Breach Notification */}
          <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Personal Data Breach Notification</h2>
            <p className="text-gray-700 leading-relaxed">
              If a personal data breach occurs, we assess its impact and take the necessary remedial actions. Where required by law, we will notify the competent authority and affected individuals in accordance with applicable timelines and procedures
            </p>
          </section>

          {/* Complaints */}
          <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">14. Complaints</h2>
            <p className="text-gray-700 leading-relaxed">
              If you have a privacy question or complaint, please contact us using the details below and we will work to resolve it. If you are not satisfied with our response, you have the right to lodge a complaint with the Competent Authority, the Saudi Data and Artificial Intelligence Authority (SDAIA), via its official channels at <span className="font-semibold">sdaia.gov.sa</span>.
            </p>
          </section>

          {/* Updates to This Policy */}
          <section>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">15. Updates to This Policy</h2>
            <div className="bg-gray-800 text-white rounded-lg p-6">
              <p className="leading-relaxed">
                We may update this Policy from time to time. We will notify you of material changes through the App or other appropriate means, and we will update the "Last Updated" date above.
              </p>
              <p className="mt-4 text-teal-300 font-semibold">
                Your continued use of our Services after any changes indicates your acceptance of the updated Privacy Policy.
              </p>
            </div>
          </section>

          {/* Controller Contact Details */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">16. Contact Details</h2>
            <div className="bg-gray-50 rounded-lg p-6 space-y-4">
              <div>
                <p className="font-semibold text-gray-900 text-lg">VITRA</p>
                <span>Developer Name: ADVANCED COMMUNICATIONS AND ELECTRONICS SYSTEMS COMPANY (ACES)</span>
                <div className="mt-3 space-y-2 text-gray-700">
                  <p className="flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-teal-600" strokeWidth={2} aria-hidden="true" />
                    <span>Address: KSA</span>
                  </p>
                  <p className="flex items-center gap-2">
                    <Mail className="w-5 h-5 text-teal-600" strokeWidth={2} aria-hidden="true" />
                    <span>Privacy Email: androidadmin@aces-co.com · appleadmin@aces-co.com</span>
                  </p>
                  {/* <p className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    <span>Phone:</span> 
                  </p> */}
                </div>
              </div>
              
              <div className="pt-4 border-t border-gray-200">
                <p className="font-semibold text-gray-900">Data Protection Officer (if appointed/where required):</p>
                <p className="text-gray-700 mt-1">Email: androidadmin@aces-co.com · appleadmin@aces-co.com</p>
                <p className="text-gray-600 text-sm mt-2 italic">
                  We may appoint a DPO where our core activities involve large-scale processing of sensitive personal data (such as health data) or where required by applicable regulations.
                </p>
              </div>
            </div>
          </section>

          <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
              <p className="text-blue-800 text-sm">
                <strong>Note:</strong> This Policy is available in Arabic and English. In case of inconsistency, the Arabic version shall prevail.
              </p>
          </div>

        </div>
      </main>
      <Footer />
    </div>
  );
};

export default PrivacyPage;

